import {
  DownOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import { Modal } from 'antd'
import { memo, Suspense, useEffect, useState } from 'react'

import UpdatedAtTooltip from '@/components/Feed/UpdatedAtTooltip/UpdatedAtTooltip'
import { SCENARIOS } from '@/constants'
import DeliveryTimeHint from '@/pages/Feed/DeliveryTimeHint/DeliveryTimeHint'
import { getColumnsKeys, getColumnValue } from '@/pages/Feed/Feed.config'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { FormattedFeedProductType } from '@/types'
import { safeLazy } from '@/utils/app/safeLazy'
import { isBuyerUser } from '@/utils/auth/userRoles'
import { IN_STOCK_DELIVERY_LABEL } from '@/utils/format/formatDeliveryTime'
import { formatStockQuantity } from '@/utils/format/formatStockQuantity'
import { formatPriceWithVat } from '@/utils/price/vat'
import { PRICE_UNAVAILABLE_TEXT } from '@/utils/products/offerAvailability'
import { getRowOfferPrice } from '@/utils/products/offerPrice'
import { focusProductSearchInput } from '@/utils/search/ui/focusProductSearchInput'

import Image from '../../../components/Atoms/Image/Image'
import ProductInfo from '../../../components/Feed/ProductInfo/ProductInfo'
import cn from './ProductCard.module.scss'
import { areProductCardPropsEqual } from './productCardProps'
import SegmentPrice from './SegmentPrice/SegmentPrice'

const AddToCartModal = safeLazy(
  () => import('@/components/Feed/AddToCartModal/AddToCartModal')
)

type ProductCardPropsType = {
  product: FormattedFeedProductType
  columnsKeys?: string[]
  isChild?: boolean
}

const ProductImage = memo(
  ({ productCode }: { productCode: number }) => {
    return (
      <div className={cn.imageCell}>
        <Image productCode={productCode} />
      </div>
    )
  },
  (prevProps, nextProps) => prevProps.productCode === nextProps.productCode
)

ProductImage.displayName = 'ProductImage'

const CartButton = memo(
  ({ product }: { product: FormattedFeedProductType }) => {
    const [cartModalOpen] = useState(false)

    return (
      <Suspense fallback={null}>
        <AddToCartModal product={product} open={cartModalOpen} />
      </Suspense>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.product.nomenclatureDto.code ===
      nextProps.product.nomenclatureDto.code
    )
  }
)

CartButton.displayName = 'CartButton'

const PriceToggle = memo(() => {
  const isBasicPriceVisible = useFeedStore((state) => state.isBasicPriceVisible)
  const setIsBasicPriceVisible = useFeedStore(
    (state) => state.setIsBasicPriceVisible
  )
  const setIsSegmentPriceVisible = useFeedStore(
    (state) => state.setIsSegmentPriceVisible
  )

  return (
    <div
      onClick={(e) => {
        e.stopPropagation()
        const nextValue = !isBasicPriceVisible
        setIsBasicPriceVisible(nextValue)
        setIsSegmentPriceVisible(nextValue)
        focusProductSearchInput()
      }}
      className={`${cn.priceToggleMobile} ${cn.priceToggle}`}
    >
      {isBasicPriceVisible ? (
        <EyeOutlined
          className={`${cn.priceToggleIcon} ${cn.priceToggleIconVisible}`}
        />
      ) : (
        <EyeInvisibleOutlined
          className={`${cn.priceToggleIcon} ${cn.priceToggleIconHidden}`}
        />
      )}
    </div>
  )
})

PriceToggle.displayName = 'PriceToggle'

const PriceCell = memo(
  ({
    price,
    isVisible,
    isClient,
  }: {
    price: string | null
    isVisible: boolean
    isClient: boolean
  }) => {
    if (!price) return null
    return (
      <div className={cn.cell}>{isClient || isVisible ? price : '***'} BYN</div>
    )
  },
  (prevProps, nextProps) =>
    prevProps.price === nextProps.price &&
    prevProps.isVisible === nextProps.isVisible &&
    prevProps.isClient === nextProps.isClient
)

PriceCell.displayName = 'PriceCell'

function ProductCard({
  product,
  columnsKeys = [],
  isChild = false,
}: ProductCardPropsType) {
  const [showChildren, setShowChildren] = useState(false)
  const [, contextHolder] = Modal.useModal()

  const tokenRoles = useUserInfoStore((state) => state.tokenRoles)
  const selectedOrganisation = useUserInfoStore(
    (state) => state.selectedOrganisation
  )
  const scenario = useUserInfoStore((state) => state.scenario)

  const isBasicPriceVisible = useFeedStore((state) => state.isBasicPriceVisible)
  const isSegmentPriceVisible = useFeedStore(
    (state) => state.isSegmentPriceVisible
  )
  const productInfoToCartPresence = useFeedStore(
    (state) => state.productInfoToCartPresence
  )

  const isClientSelectionPending =
    scenario === SCENARIOS.CLIENT_SELECTION_PENDING
  const isClient = isBuyerUser(tokenRoles)
  const allowedKeys = getColumnsKeys(tokenRoles) || columnsKeys

  useEffect(() => {
    if (!product) return
    const hasCart = Object.keys(productInfoToCartPresence).some((key) =>
      key.startsWith(`${product.nomenclatureDto.code} `)
    )
    if (hasCart) {
      setShowChildren(true)
    }
  }, [productInfoToCartPresence, product])

  if (!product) return null

  const { article, description } = product.nomenclatureDto
  const brand = product.nomenclatureDto.brandDto?.description || 'Не указан'
  const unit = product.nomenclatureDto.unitOfMeasurementDto?.description || ''

  const price = getRowOfferPrice(product)
  const currentStock = product.suppliersStock
    ?.map((supplier) => supplier.stock?.find((st) => (st.quantity ?? 0) > 0))
    .find(Boolean)

  const stockDescription = currentStock?.storeShortModel?.description || '-'
  const quantityDisplay = formatStockQuantity(
    currentStock?.quantity,
    unit,
    currentStock?.displayRules,
    '0'
  )

  const basicPrice = price?.purchasePrice
  const showsBasicPrice = allowedKeys.includes('basicPrice')

  const retailPrice = price?.retailPrice
  const retailPriceFormatted =
    retailPrice === undefined
      ? PRICE_UNAVAILABLE_TEXT
      : formatPriceWithVat(retailPrice)

  const basicPriceFormatted =
    basicPrice === undefined ? null : formatPriceWithVat(basicPrice)

  const showCartButton =
    allowedKeys.includes('cartButton') &&
    product.suppliersStock?.[0]?.stock?.[0]?.quantity &&
    !isClientSelectionPending

  const deliveryRaw = getColumnValue(product, 'deliveryTime')
  const deliveryTime =
    typeof deliveryRaw === 'object' || deliveryRaw === undefined
      ? '-'
      : String(deliveryRaw)

  return (
    <>
      {contextHolder}
      <div className={cn.productWrapper}>
        <div
          className={[
            cn.productRow,
            showChildren ? cn.active : '',
            isChild ? cn.childRow : '',
            Object.keys(selectedOrganisation).length > 0
              ? cn.organisationSelected
              : '',
          ].join(' ')}
        >
          {!isChild && (
            <div className={cn.infoIcon}>
              <ProductInfo product={product.nomenclatureDto} />
            </div>
          )}

          {product.children?.length ? (
            <button
              className={`${cn.expandBtn} ${showChildren ? cn.open : ''}`}
              onClick={() => setShowChildren(!showChildren)}
            >
              <DownOutlined className={cn.donwIcon} />
            </button>
          ) : (
            <div className={cn.expandPlaceholder} />
          )}

          {!isChild && (
            <div className={cn.infoBlock}>
              <div className={cn.cell}>{brand}</div>
              <div className={cn.cell}>{article}</div>
              <div className={cn.cell}>{description}</div>
            </div>
          )}

          {!isChild && (
            <ProductImage productCode={product.nomenclatureDto.code} />
          )}

          <div className={cn.priceStockBlock}>
            <div className={cn.cell}>{retailPriceFormatted} BYN</div>

            <SegmentPrice
              price={price}
              isHidden={!isClient && !isSegmentPriceVisible}
              showPicker={!isChild && !isClient}
              partnerSegment={product.suppliersStock[0]?.clientSegment}
            />

            {showsBasicPrice && (
              <PriceCell
                price={basicPriceFormatted}
                isVisible={isBasicPriceVisible}
                isClient={isClient}
              />
            )}

            <div className={cn.cell}>
              {stockDescription}:{' '}
              <UpdatedAtTooltip
                updatedAt={currentStock?.stockUpdatedAt}
                kind="stock"
              >
                {quantityDisplay}
              </UpdatedAtTooltip>
            </div>
            <div className={cn.cell}>
              Срок доставки:{' '}
              {deliveryTime === IN_STOCK_DELIVERY_LABEL ? (
                <span className={cn.inStockDelivery}>{deliveryTime}</span>
              ) : deliveryTime !== '-' ? (
                <DeliveryTimeHint className={cn.deliveryTimeHint}>
                  {deliveryTime}
                </DeliveryTimeHint>
              ) : (
                deliveryTime
              )}
            </div>
          </div>

          <div className={cn.cell}>
            {!isChild && !isClient && <PriceToggle />}
            {showCartButton && <CartButton product={product} />}
          </div>
        </div>

        {product.children && product.children.length > 0 && (
          <div
            className={`${cn.childrenContainer} ${showChildren ? cn.open : ''}`}
          >
            {product.children.map((child, index) => (
              <ProductCard
                key={`${child.nomenclatureDto.code}-${index}`}
                product={child}
                isChild={true}
                columnsKeys={columnsKeys}
              />
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export default memo(ProductCard, areProductCardPropsEqual)
